import { transliterate } from './translit';

/**
 * String Cleaning Utilities for Album Art Service
 * Handles artist/track name normalization and matching
 */

// Known band names where & is part of the official name (should NOT be split)
export const BAND_NAMES_WITH_AMPERSAND = [
    'florence & the machine',
    'florence and the machine',
    'mumford & sons',
    'simon & garfunkel',
    'hall & oates',
    'earth, wind & fire',
    'earth wind & fire',
    'tom & jerry',
    'belle & sebastian',
    'tegan & sara',
    'tears for fears',
    'peter, bjorn & john',
    'emerson, lake & palmer',
    'crosby, stills & nash',
    'crosby, stills, nash & young',
    'the mamas & the papas',
    'sam & dave',
    'run-d.m.c.',
    'above & beyond',
    'banks & steelz',
    'dan + shay',
    'mgmt',
];

/**
 * Check if the artist name is a known band that shouldn't be split
 */
const isBandWithAmpersand = (artist) => {
    if (!artist) return false;
    const lower = artist.toLowerCase().trim();
    return BAND_NAMES_WITH_AMPERSAND.some(band => lower.includes(band) || band.includes(lower));
};

// Known abbreviation mappings for Russian artists
export const ARTIST_ABBREVIATIONS = {
    'СБПЧ': 'Самое Большое Простое Число',
    'ДДТ': 'DDT',
    'БИ-2': 'Би-2',
    'АК-47': 'AK-47',
    'ГРОТ': 'Грот',
    'СПЛИН': 'Сплин',
    'КИНО': 'Кино',
    'КИПЕЛОВ': 'Кипелов',
    'PINK': 'P!NK',
};

/**
 * Clean artist name
 * Rules:
 * - Take string up to first separator: &, feat, feat., vs, ft, ft., and, /, (, [, featuring, pres, presents
 * - Remove "Pres." or "Presents" at start
 * - Trim spaces
 */
export const cleanArtistName = (artist) => {
    if (!artist) return '';

    let cleaned = artist.trim();

    // Check for known abbreviations (case insensitive)
    const upperCleaned = cleaned.toUpperCase();
    for (const [abbr, full] of Object.entries(ARTIST_ABBREVIATIONS)) {
        if (upperCleaned === abbr.toUpperCase()) {
            cleaned = full;
            break;
        }
    }

    // Remove Pres./Presents at start (case insensitive)
    cleaned = cleaned.replace(/^Pres\.?\s+|^Presents\s+/i, '');

    // Don't split known bands with & in their name (like "Florence & The Machine")
    if (isBandWithAmpersand(cleaned)) {
        return cleaned.trim();
    }

    // Split by separators: &, and, feat, ft, vs, featuring, with, pres, presents, comma, slash, brackets
    // IMPROVED: Added & and "and" as primary separators for collaboration tracks
    const separatorsRegex = /\s*[&]\s*|\s+(?:feat\.?|ft\.?|vs\.?|featuring|with|pres\.?|presents|and)\s+|[,/([]/i;

    const parts = cleaned.split(separatorsRegex);
    if (parts.length > 0) {
        cleaned = parts[0];
    }

    return cleaned.trim();
};

/**
 * Get all artist variants for fallback searching
 * e.g., "WAWA & HERD" → ["WAWA & HERD", "WAWA", "HERD"]
 */
export const getArtistVariants = (artist) => {
    if (!artist) return [];
    
    const variants = [artist.trim()];
    
    // Don't split known bands with & in their name (like "Florence & The Machine")
    if (isBandWithAmpersand(artist)) {
        return variants;
    }
    
    // Split by & or / and add individual artists
    const parts = artist.split(/\s*[&/]\s*/).filter(p => p.trim());
    if (parts.length > 1) {
        parts.forEach(part => {
            const trimmed = part.trim();
            if (trimmed && !variants.includes(trimmed)) {
                variants.push(trimmed);
            }
        });
    }
    
    return variants;
};

/**
 * Clean track name
 * Rules:
 * - Remove text in (...) and [...]
 * - Remove words RMX and REMIX
 * - Trim spaces
 */
export const cleanTrackName = (track) => {
    if (!track) return '';

    let cleaned = track;

    // Remove text in parentheses (...) or brackets [...]
    cleaned = cleaned.replace(/\s*\(.*?\)\s*/g, ' ');
    cleaned = cleaned.replace(/\s*\[.*?\]\s*/g, ' ');

    // Normalize spaces
    cleaned = cleaned.replace(/\s+/g, ' ').trim();

    // Remove separate words RMX, REMIX, EDIT, MIX, RADIO, VERSION, etc. (case insensitive)
    cleaned = cleaned.replace(/\b(RMX|REMIX|EDIT|MIX|RADIO|VERSION|REMASTER|LIVE|ACOUSTIC|SINGLE|EP|MAXI|LP|PREVIEW|DEMO)\b/gi, '');

    // Remove trailing dashes and everything after if it looks like a suffix
    cleaned = cleaned.replace(/\s*-\s*(RMX|REMIX|EDIT|MIX|RADIO|VERSION|REMASTER|LIVE|ACOUSTIC|SINGLE|EP|MAXI|LP|PREVIEW|DEMO).*$/i, '');

    return cleaned.replace(/\s+/g, ' ').trim();
};

/**
 * De-accent/Normalize special characters to basic Latin (É -> E, Ø -> O)
 */
export const deaccent = (str) => {
    if (!str) return '';
    // Use modern JS normalization to separate accents, then strip them
    const normalized = str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    // Handle special cases not covered by NFD (like Ø)
    return normalized
        .replace(/ø/g, 'o')
        .replace(/ð/g, 'd')
        .replace(/þ/g, 'th')
        .replace(/æ/g, 'ae')
        .replace(/œ/g, 'oe');
};

/**
 * Normalize string for comparison (lowercase, remove non-alphanumeric)
 */
export const normalize = (str) => {
    if (!str) return '';
    // 1. Deaccent first (JACOTÈNE -> JACOTENE)
    let res = deaccent(str.toLowerCase());

    // 2. Normalize & to and, then remove everything except a-z, 0-9 and Cyrillic
    res = res.replace(/&/g, 'and');
    res = res.replace(/[^a-z0-9а-я]/g, '');

    if (res.startsWith('the')) {
        res = res.substring(3);
    }
    return res;
};

/**
 * Check if two artist names match (with fuzzy/transliteration support)
 */
export const isArtistMatch = (found, requested) => {
    if (!found || !requested) return false;
    const fNorm = normalize(found);
    const rNorm = normalize(requested);
    const fTrans = normalize(transliterate(found));
    const rTrans = normalize(transliterate(requested));

    return fNorm.includes(rNorm) || rNorm.includes(fNorm) ||
        fNorm.includes(rTrans) || rTrans.includes(fNorm) ||
        fTrans.includes(rNorm) || rNorm.includes(fTrans) ||
        fTrans.includes(rTrans) || rTrans.includes(fTrans);
};

/**
 * Check if URL is a valid album art (not a placeholder)
 */
export const isValidArtwork = (url) => {
    if (!url) return false;
    const placeholderPatterns = [
        'music-note',
        'default',
        'placeholder',
        '1x1',
        'star',
        'unavailable',
        'generic',
        'missing',
        '2a96cbd8b46e442fc41c2b86b821562f' // Last.FM Star Placeholder Hash
    ];
    const lowerUrl = url.toLowerCase();
    return !placeholderPatterns.some(p => lowerUrl.includes(p));
};
